import type {
    ForwardRefExoticComponent,
    MemoExoticComponent,
    RefAttributes,
    SVGProps,
} from "react"
import React from "react"
import { t } from "@lingui/core/macro"
import { SendArrow, Tokens } from "../../../common/atoms/Icons/Chat"
import { ClickableTooltip } from "../../../common/molecules/ClickableTooltip"
import { Placement } from "../../../common/molecules/ClickableTooltip/ClickableTooltip"
import { IconButton } from "../../common/IconButton/IconButton"
import { classNames } from "../../utils"
import styles from "./SubmitFormIcon.module.scss"

const BTN_ID_SUBMIT = "submit-btn"
const BTN_ID_TOKENS = "tokens-btn"
const BTN_ID_TOKENS_GREY = "tokens-grey-btn"
const SUB_ID_SUBMIT = "submit-icon"
const SUB_ID_TOKENS = "tokens-icon"
const SUB_ID_TOKENS_GREY = "tokens-grey-icon"

type Props = {
    isLoading: boolean
    canSend: boolean
    canTip: boolean
    handleSendMessage: () => void
    handleSendTokens: () => void
}

enum DisplayCases {
    Unkonwn,
    LoadingCantSend,
    LoadingCanSend,
    NotLoadingCanSend,
    NotLoadingCantSendCanTip,
    NotLoadingCantSendCantTip,
}

interface IButtonConfigProps {
    butonId: string
    iconAlt: string
    subId: string
    // TODO: https://multimediallc.atlassian.net/browse/MSG-461 - this will let us replace "any" with a specific interface
    subType: MemoExoticComponent<
        ForwardRefExoticComponent<
            /* eslint-disable @typescript-eslint/no-redundant-type-constituents */
            Omit<SVGProps<SVGSVGElement> & any, "ref"> &
                RefAttributes<SVGSVGElement>
        >
    >
    subStyles?: string
    tooltipText?: string
    onClick?: () => void
}

/*
 * Helper for handling the submit icon in the MessageInputBar.
 * Cases to handle in order:
 * 1. Loading, cannot send (no text) - disabled token icon
 * 2. Loading, can send (has text) - disabled send icon w/ tooltip
 * 3. Not loading, can send - enabled send icon
 * 4. Not loading, cannot send, can tip - enabled token icon
 * 5. Not loading, cannot send, cannot tip - disabled token icon w/ tooltip
 */
export function SubmitFormIcon({
    isLoading,
    canSend,
    canTip,
    handleSendMessage,
    handleSendTokens,
}: Props) {
    const ALT_SEND_TOKENS = t`Send tokens`
    const ALT_SEND_MESSAGE = t`Send message`

    const TOOLTIP_DISABLED_TOKENS = t`This user may not receive tokens.`
    const TOOLTIP_LOADING_MESSAGE = t`Please wait — Messages are still loading.`

    // Initialize the different case configurations.
    const icon_config: { [id: string]: IButtonConfigProps } = {
        [DisplayCases.Unkonwn]: {
            // Defaults to the first known case.
            butonId: BTN_ID_TOKENS,
            iconAlt: ALT_SEND_TOKENS,
            subType: Tokens,
            subId: SUB_ID_TOKENS_GREY,
            subStyles: classNames(styles.tokens, styles.disabled),
        },
        [DisplayCases.LoadingCantSend]: {
            butonId: BTN_ID_TOKENS,
            iconAlt: ALT_SEND_TOKENS,
            subType: Tokens,
            subId: SUB_ID_TOKENS_GREY,
            subStyles: classNames(styles.tokens, styles.disabled),
        },
        [DisplayCases.LoadingCanSend]: {
            butonId: BTN_ID_SUBMIT,
            iconAlt: ALT_SEND_MESSAGE,
            subType: SendArrow,
            subId: SUB_ID_SUBMIT,
            subStyles: classNames(styles.arrow, styles.disabled),
            tooltipText: TOOLTIP_LOADING_MESSAGE,
        },
        [DisplayCases.NotLoadingCanSend]: {
            butonId: BTN_ID_SUBMIT,
            iconAlt: ALT_SEND_MESSAGE,
            subType: SendArrow,
            subId: SUB_ID_SUBMIT,
            subStyles: classNames(styles.arrow),
            tooltipText: "",
            onClick: handleSendMessage,
        },
        [DisplayCases.NotLoadingCantSendCanTip]: {
            butonId: BTN_ID_TOKENS,
            iconAlt: ALT_SEND_TOKENS,
            subType: Tokens,
            subId: SUB_ID_TOKENS,
            subStyles: classNames(styles.tokens),
            tooltipText: "",
            onClick: handleSendTokens,
        },
        [DisplayCases.NotLoadingCantSendCantTip]: {
            butonId: BTN_ID_TOKENS_GREY,
            iconAlt: ALT_SEND_TOKENS,
            subType: Tokens,
            subId: SUB_ID_TOKENS,
            subStyles: classNames(styles.tokens, styles.disabled),
            tooltipText: TOOLTIP_DISABLED_TOKENS,
            onClick: handleSendTokens, // Used for NewRelic event tracking of disabled token icon
        },
    }

    // Determine which condition to select based on isLoading, canSend, and canTip.
    let condition: DisplayCases
    if (isLoading && !canSend) {
        condition = DisplayCases.LoadingCantSend
    } else if (isLoading && canSend) {
        condition = DisplayCases.LoadingCanSend
    } else if (!isLoading && canSend) {
        condition = DisplayCases.NotLoadingCanSend
    } else if (!isLoading && !canSend && canTip) {
        condition = DisplayCases.NotLoadingCantSendCanTip
    } else if (!isLoading && !canSend && !canTip) {
        condition = DisplayCases.NotLoadingCantSendCantTip
    } else {
        condition = DisplayCases.Unkonwn
    }
    const config = icon_config[condition]

    return (
        <ClickableTooltip
            text={config.tooltipText}
            toolTipClassName={styles.tooltipOverride}
            placement={Placement.top}
        >
            <IconButton
                data-testid={config.butonId}
                className={styles.iconButton}
                icon={React.createElement(config.subType, {
                    "data-testid": config.subId,
                    className: config.subStyles,
                })}
                onClick={config.onClick}
                iconAlt={config.iconAlt}
            />
        </ClickableTooltip>
    )
}
